import PropTypes from 'prop-types';
import _extends from '@babel/runtime/helpers/esm/extends';
import _inheritsLoose from '@babel/runtime/helpers/esm/inheritsLoose';
import React, { Component } from 'react';
import { createGenerateId, create, getDynamicStyles, SheetsManager } from 'jss';
export { SheetsRegistry, createGenerateId } from 'jss';
import preset from 'jss-preset-default';
import _createClass from '@babel/runtime/helpers/esm/createClass';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/esm/objectWithoutPropertiesLoose';
import { ThemeContext } from 'theming';
export { ThemeProvider, withTheme, createTheming } from 'theming';
import hoistNonReactStatics from 'hoist-non-react-statics';
/**
 * Namespaces to avoid conflicts on the context.
 */

var jss = '64a55d578f856d258dc345b094a2a2b3';
var sheetsRegistry = 'd4bd0baacbc52bbd48bbb9eb24344ecd';
var managers = 'b768b78919504fba9de2c03545c5cd3a';
var sheetOptions = '6fc570d6bd61383819d0f9e7407c452d';
var propTypes = {
  jss: PropTypes.shape({
    options: PropTypes.shape({
      createGenerateId: PropTypes.func.isRequired
    }).isRequired,
    createStyleSheet: PropTypes.func.isRequired,
    removeStyleSheet: PropTypes.func.isRequired
  }),
  registry: PropTypes.shape({
    add: PropTypes.func.isRequired,
    toString: PropTypes.func.isRequired
  })
};

var _ns$jss$ns$sheetOptio;

var contextTypes = (_ns$jss$ns$sheetOptio = {}, _ns$jss$ns$sheetOptio[jss] = propTypes.jss, _ns$jss$ns$sheetOptio[sheetOptions] = PropTypes.object, _ns$jss$ns$sheetOptio[sheetsRegistry] = propTypes.registry, _ns$jss$ns$sheetOptio[managers] = PropTypes.object, _ns$jss$ns$sheetOptio);

var JssProvider =
/*#__PURE__*/
function (_Component) {
  _inheritsLoose(JssProvider, _Component);

  function JssProvider() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _Component.call.apply(_Component, [this].concat(args)) || this;
    _this.registry = void 0;
    _this.managers = void 0;
    _this.generateId = void 0;
    return _this;
  }

  var _proto = JssProvider.prototype; // JssProvider can be nested. We allow to overwrite any context prop at any level.
  // 1. Check if there is a value passed over props.
  // 2. If value was passed, we set it on the child context.
  // 3. If value was not passed, we proxy parent context (default context behaviour).

  _proto.getChildContext = function getChildContext() {
    var _context;

    var _this$props = this.props,
        registry = _this$props.registry,
        classNamePrefix = _this$props.classNamePrefix,
        localJss = _this$props.jss,
        generateId = _this$props.generateId,
        disableStylesGeneration = _this$props.disableStylesGeneration; // eslint-disable-next-line react/react/destructuring-assignment

    var sheetOptions$$1 = this.context[sheetOptions] || {};
    var context = (_context = {}, _context[sheetOptions] = sheetOptions$$1, _context);

    if (registry) {
      context[sheetsRegistry] = registry; // This way we identify a new request on the server, because user will create
      // a new Registry instance for each.

      if (registry !== this.registry) {
        // We reset managers because we have to regenerate all sheets for the new request.
        this.managers = {};
        this.registry = registry;
      }
    } // Make sure we don't loose managers when JssProvider is used inside of a stateful
    // component which decides to rerender.


    context[managers] = this.managers;

    if (generateId) {
      sheetOptions$$1.generateId = generateId;
    } else if (!sheetOptions$$1.generateId) {
      if (!this.generateId) {
        var createGenerateId$$1 = createGenerateId;

        if (localJss && localJss.options.createGenerateId) {
          createGenerateId$$1 = localJss.options.createGenerateId;
        } // Make sure we don't loose the generator when JssProvider is used inside of a stateful
        // component which decides to rerender.


        this.generateId = createGenerateId$$1();
      }

      sheetOptions$$1.generateId = this.generateId;
    }

    if (classNamePrefix) sheetOptions$$1.classNamePrefix = classNamePrefix;
    if (localJss) context[jss] = localJss;

    if (disableStylesGeneration !== undefined) {
      sheetOptions$$1.disableStylesGeneration = disableStylesGeneration;
    }

    return context;
  };

  _proto.render = function render() {
    return this.props.children;
  };

  return JssProvider;
}(Component);

JssProvider.propTypes = _extends({}, propTypes, {
  generateId: PropTypes.func,
  classNamePrefix: PropTypes.string,
  disableStylesGeneration: PropTypes.bool,
  children: PropTypes.node.isRequired
});
JssProvider.childContextTypes = contextTypes;
JssProvider.contextTypes = contextTypes;
var defaultJss = create(preset());
/**
 * Adds `composes` property to each top level rule
 * in order to have a composed class name for dynamic style sheets.
 *
 * It relies on jss-plugin-compose and jss-plugin-extend plugins.
 *
 * Example:
 * classes:  {left: 'a', button: 'b'}
 * styles:   {button: {height: () => { ... }}}
 * composed: {
 *   button: {
 *     composes: 'b',
 *     height: () => { ... }
 *   },
 *   left: {
 *     composes: 'a'
 *   }
 * }
 */

var compose = function compose(staticClasses, dynamicClasses) {
  var combinedClasses = _extends({}, staticClasses);

  for (var name in dynamicClasses) {
    combinedClasses[name] = staticClasses[name] ? staticClasses[name] + " " + dynamicClasses[name] : dynamicClasses[name];
  }

  return combinedClasses;
};

var getDisplayName = function getDisplayName(Component$$1) {
  return Component$$1.displayName || Component$$1.name || 'Component';
}; // Like a Symbol


var dynamicStylesNs = Math.random();
/*
 * # Use cases
 *
 * - Unthemed component accepts styles object
 * - Themed component accepts styles creator function which takes theme as a single argument
 * - Multiple instances will re-use the same static sheet via sheets manager
 * - Sheet manager identifies static sheets by theme as a key
 * - For unthemed components theme is an empty object
 * - The very first instance will add static sheet to sheets manager
 * - Every further instances will get that static sheet from sheet manager
 * - Every mount of every instance will call method `sheetsManager.manage`,
 * thus incrementing reference counter.
 * - Every unmount of every instance will call method `sheetsManager.unmanage`,
 * thus decrementing reference counter.
 * - `sheetsManager.unmanage` under the hood will detach static sheet once reference
 * counter is zero.
 * - Dynamic styles are not shared between instances
 *
 */

var getStyles = function getStyles(stylesOrCreator, theme) {
  if (typeof stylesOrCreator !== 'function') {
    return stylesOrCreator;
  }

  return stylesOrCreator(theme);
}; // Returns an object with array property as a key and true as a value.


var toMap = function toMap(arr) {
  return arr.reduce(function (map, prop) {
    var _extends2;

    return _extends({}, map, (_extends2 = {}, _extends2[prop] = true, _extends2));
  }, {});
};

var defaultInjectProps = {
  sheet: false,
  classes: true,
  theme: true
};
var managersCounter = 0;

function createHOC(stylesOrCreator, InnerComponent, options) {
  var isThemingEnabled = typeof stylesOrCreator === 'function';

  var theming = options.theming,
      inject = options.inject,
      optionsJss = options.jss,
      sheetOptions$$1 = _objectWithoutPropertiesLoose(options, ["theming", "inject", "jss"]);

  var injectMap = inject ? toMap(inject) : defaultInjectProps;
  var displayName = getDisplayName(InnerComponent);
  var defaultClassNamePrefix = process.env.NODE_ENV === 'production' ? '' : displayName + "-";
  var noTheme = {};
  var managerId = managersCounter++;
  var manager = new SheetsManager(); // $FlowFixMe: DefaultProps is missing in type definitions

  var _InnerComponent$defau = _extends({}, InnerComponent.defaultProps),
      _InnerComponent$defau2 = _InnerComponent$defau.classes,
      defaultClasses = _InnerComponent$defau2 === void 0 ? {} : _InnerComponent$defau2,
      defaultProps = _objectWithoutPropertiesLoose(_InnerComponent$defau, ["classes"]);

  var Jss =
  /*#__PURE__*/
  function (_Component) {
    _inheritsLoose(Jss, _Component);

    function Jss(props, context) {
      var _this;

      _this = _Component.call(this, props, context) || this;
      _this.classNamePrefix = defaultClassNamePrefix;
      _this.context = void 0;
      _this.context = context;
      var contextSheetOptions = context[sheetOptions];

      if (contextSheetOptions && contextSheetOptions.classNamePrefix) {
        _this.classNamePrefix = contextSheetOptions.classNamePrefix + _this.classNamePrefix;
      }

      _this.state = _this.createState();

      _this.manage(_this.state);

      return _this;
    }

    var _proto = Jss.prototype;

    _proto.componentDidUpdate = function componentDidUpdate(prevProps, prevState) {
      var dynamicSheet = this.state.dynamicSheet;
      if (dynamicSheet) dynamicSheet.update(this.props);

      if (isThemingEnabled && this.props.theme !== prevProps.theme) {
        var newState = this.createState();
        this.manage(newState);
        this.unmanage(prevProps, prevState); // eslint-disable-next-line react/no-did-update-set-state

        this.setState(newState);
      }
    };

    _proto.componentWillUnmount = function componentWillUnmount() {
      this.unmanage(this.props, this.state);
    };

    _proto.getStaticSheet = function getStaticSheet() {
      var theme = this.theme;
      var staticSheet = this.manager.get(theme);

      if (staticSheet) {
        return staticSheet;
      }

      var contextSheetOptions = this.context[sheetOptions];
      var styles = getStyles(stylesOrCreator, theme);
      staticSheet = this.jss.createStyleSheet(styles, _extends({}, sheetOptions$$1, contextSheetOptions, {
        meta: displayName + ", " + (isThemingEnabled ? 'Themed' : 'Unthemed') + ", Static",
        classNamePrefix: this.classNamePrefix
      }));
      this.manager.add(theme, staticSheet); // $FlowFixMe Cannot add random fields to instance of class StyleSheet

      staticSheet[dynamicStylesNs] = getDynamicStyles(styles);
      return staticSheet;
    };

    _proto.getDynamicSheet = function getDynamicSheet(staticSheet) {
      // $FlowFixMe Cannot access random fields on instance of class StyleSheet
      var dynamicStyles = staticSheet[dynamicStylesNs];
      if (!dynamicStyles) return undefined;
      var contextSheetOptions = this.context[sheetOptions];
      return this.jss.createStyleSheet(dynamicStyles, _extends({}, sheetOptions$$1, contextSheetOptions, {
        meta: displayName + ", " + (isThemingEnabled ? 'Themed' : 'Unthemed') + ", Dynamic",
        classNamePrefix: this.classNamePrefix,
        link: true
      }));
    };

    _proto.manage = function manage(state) {
      var dynamicSheet = state.dynamicSheet,
          staticSheet = state.staticSheet;
      var registry = this.context[sheetsRegistry];
      this.manager.manage(this.theme);

      if (staticSheet && registry) {
        registry.add(staticSheet);
      }

      if (dynamicSheet) {
        dynamicSheet.update(this.props).attach();

        if (registry) {
          registry.add(dynamicSheet);
        }
      }
    };

    _proto.unmanage = function unmanage(prevProps, prevState) {
      this.manager.unmanage(prevProps.theme);

      if (prevState.dynamicSheet) {
        this.jss.removeStyleSheet(prevState.dynamicSheet);
      }
    };

    _proto.computeClasses = function computeClasses(staticSheet, dynamicSheet) {
      var jssClasses = dynamicSheet ? compose(staticSheet.classes, dynamicSheet.classes) : staticSheet.classes;
      return _extends({}, defaultClasses, jssClasses, this.props.classes);
    };

    _proto.createState = function createState() {
      var contextSheetOptions = this.context[sheetOptions];

      if (contextSheetOptions && contextSheetOptions.disableStylesGeneration) {
        return {
          classes: {}
        };
      }

      var staticSheet = this.getStaticSheet();
      var dynamicSheet = this.getDynamicSheet(staticSheet);
      return {
        staticSheet: staticSheet,
        dynamicSheet: dynamicSheet,
        classes: this.computeClasses(staticSheet, dynamicSheet)
      };
    };

    _proto.render = function render() {
      var _this$state = this.state,
          dynamicSheet = _this$state.dynamicSheet,
          classes = _this$state.classes,
          staticSheet = _this$state.staticSheet; // $FlowFixMe: Flow complains for no reason...

      var _this$props = this.props,
          innerRef = _this$props.innerRef,
          theme = _this$props.theme,
          props = _objectWithoutPropertiesLoose(_this$props, ["innerRef", "theme"]);

      var sheet = dynamicSheet || staticSheet;
      if (injectMap.sheet && !props.sheet && sheet) props.sheet = sheet;
      if (injectMap.theme) props.theme = theme; // We have merged classes already.

      if (injectMap.classes) props.classes = classes;
      return React.createElement(InnerComponent, _extends({
        ref: innerRef
      }, props));
    };

    _createClass(Jss, [{
      key: "theme",
      get: function get() {
        return isThemingEnabled ? this.props.theme : noTheme;
      }
    }, {
      key: "jss",
      get: function get() {
        return this.context[jss] || optionsJss || defaultJss;
      }
    }, {
      key: "manager",
      get: function get() {
        var managers$$1 = this.context[managers]; // If `managers` map is present in the context, we use it in order to
        // let JssProvider reset them when new response has to render server-side.

        if (managers$$1) {
          if (!managers$$1[managerId]) {
            managers$$1[managerId] = new SheetsManager();
          }

          return managers$$1[managerId];
        }

        return manager;
      }
    }]);

    return Jss;
  }(Component);

  Jss.displayName = "Jss(" + displayName + ")";
  Jss.InnerComponent = InnerComponent;
  Jss.contextTypes = contextTypes;
  Jss.propTypes = {
    innerRef: PropTypes.func
  };
  Jss.defaultProps = _extends({}, defaultProps, {
    theme: noTheme
  });

  if (isThemingEnabled || injectMap.theme) {
    // eslint-disable-next-line no-inner-declarations
    var ContextSubscribers = function ContextSubscribers(props) {
      return React.createElement(ThemeConsumer, null, function (theme) {
        return React.createElement(Jss, _extends({
          theme: theme
        }, props));
      });
    };

    var ThemeConsumer = theming && theming.context.Consumer || ThemeContext.Consumer;
    ContextSubscribers.InnerComponent = InnerComponent;
    return ContextSubscribers;
  }

  return Jss;
}
/**
 * Global index counter to preserve source order.
 * As we create the style sheet during componentWillMount lifecycle,
 * children are handled after the parents, so the order of style elements would
 * be parent->child. It is a problem though when a parent passes a className
 * which needs to override any childs styles. StyleSheet of the child has a higher
 * specificity, because of the source order.
 * So our solution is to render sheets them in the reverse order child->sheet, so
 * that parent has a higher specificity.
 */


var indexCounter = -100000;

var NoRenderer = function NoRenderer(props) {
  return props.children || null;
};
/**
 * HOC creator function that wrapps the user component.
 *
 * `injectSheet(styles, [options])(Component)`
 */


function injectSheet(styles, options) {
  if (options === void 0) {
    options = {};
  }

  var index = options.index === undefined ? indexCounter++ : options.index;
  return function (InnerComponent) {
    if (InnerComponent === void 0) {
      InnerComponent = NoRenderer;
    }

    var Jss = createHOC(styles, InnerComponent, _extends({}, options, {
      index: index
    }));
    return hoistNonReactStatics(Jss, InnerComponent, {
      inner: true
    });
  };
}

export default injectSheet;
export { JssProvider, defaultJss as jss };